import { CustomerForm } from "@/components/CustomerForm";
import { CustomerFormData } from "@/components/forms/types";
import { CartSummary } from "@/components/CartSummary";
import { QRPhButton } from "@/components/QRPhButton";
import { Event } from "@/lib/types";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useState } from "react";

interface PaymentSectionProps {
  event: Event;
  seoUrl: string;
  selectedSeats: string[];
  vipTicketCount?: number;
  standingTickets: number;
  earlyBirdTickets: number;
  discountPercentage: number;
  setDiscountPercentage: (percentage: number) => void;
  onBeginCheckout?: () => void;
}

export const PaymentSection = ({
  event,
  seoUrl,
  selectedSeats,
  vipTicketCount = 0,
  standingTickets,
  earlyBirdTickets,
  discountPercentage,
  setDiscountPercentage,
  onBeginCheckout,
}: PaymentSectionProps) => {
  const [currentTotal, setCurrentTotal] = useState(0);

  const handleCustomerSubmit = async (data: CustomerFormData) => {
    // Trigger begin_checkout event before processing payment
    if (onBeginCheckout) {
      onBeginCheckout();
    }

    // Log detailed payment data for debugging
    console.log('Payment Details:', {
      eventId: event.id,
      eventTitle: event.title,
      template_type: event.template_type,
      vipSeats: selectedSeats,
      vipTicketCount,
      standingTickets,
      earlyBirdTickets,
      currentTotal,
      stripeAmountCents: Math.round(currentTotal * 100),
      currency: event.currency || 'AUD',
      tax_behavior: event.tax_behavior || 'inclusive',
      discountPercentage,
      priceBreakdown: {
        vipPrice: event.price_vip,
        standingPrice: event.price_general_admission,
        earlyBirdPrice: event.early_bird_price,
        slayFeePercentage: event.slay_fee_percentage,
        slayFeeFixed: event.slay_fee_fixed,
        gstRate: event.inclusive_gst_rate
      }
    });

    // Validate that at least one ticket is selected
    const vipQuantity = event.template_type === 'no_seating' ? selectedSeats.length : vipTicketCount;
    if (!event.id || (vipQuantity === 0 && standingTickets === 0 && earlyBirdTickets === 0)) {
      toast.error("Please select at least one ticket");
      return;
    }

    // Validate VIP ticket availability
    if (vipQuantity > 0 && vipQuantity > (event.quantity_vip || 0)) {
      toast.error("Selected VIP tickets are no longer available");
      return;
    }

    // Validate Standing ticket availability
    if (standingTickets > 0 && standingTickets > (event.quantity_standing || 0)) {
      toast.error("Selected standing tickets are no longer available");
      return;
    }

    // Validate Early Bird ticket availability
    if (earlyBirdTickets > 0 && earlyBirdTickets > (event.quantity_early_bird || 0)) {
      toast.error("Selected early bird tickets are no longer available");
      return;
    }

    // Convert to cents for Stripe
    const stripeAmount = Math.round(currentTotal * 100);

    // Build ticket description
    const ticketParts = [];
    if (vipQuantity > 0) {
      ticketParts.push(`${vipQuantity} VIP ticket${vipQuantity > 1 ? 's' : ''}`);
    }
    if (standingTickets > 0) {
      ticketParts.push(`${standingTickets} GA ticket${standingTickets > 1 ? 's' : ''}`);
    }
    if (earlyBirdTickets > 0) {
      ticketParts.push(`${earlyBirdTickets} Early Bird ticket${earlyBirdTickets > 1 ? 's' : ''}`);
    }
    const ticketDescription = ticketParts.join(", ");

    console.log('Initiating checkout with:', {
      vipQuantity,
      standingTickets,
      earlyBirdTickets,
      discountPercentage,
      finalTotal: currentTotal,
      stripeAmountCents: Math.round(currentTotal * 100),
      ticketDescription,
      currency: event.currency,
      tax_behavior: event.tax_behavior,
      checkoutData: {
        vipSeats: selectedSeats,
        vipTicketCount: event.template_type === 'vip_seating' ? vipTicketCount : undefined,
        standingTickets,
        earlyBirdTickets,
        eventId: event.id,
        customerEmail: data.email,
        totalAmount: Math.round(currentTotal * 100),
        discountPercentage,
        currency: event.currency || 'AUD',
        tax_behavior: event.tax_behavior || 'inclusive'
      }
    });

    try {
      const { data: checkoutData, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          vipSeats: selectedSeats,
          vipTicketCount: event.template_type === 'vip_seating' ? vipTicketCount : undefined,
          standingTickets,
          earlyBirdTickets,
          eventId: event.id,
          customerInfo: data,
          totalAmount: Math.round(currentTotal * 100),
          discountPercentage,
          currency: event.currency || 'AUD',
          tax_behavior: event.tax_behavior || 'inclusive',
          customer_email: data.email,
          ticketDescription
        },
      });

      if (error) {
        console.error('Checkout error:', error);
        throw error;
      }

      if (checkoutData?.url) {
        console.log('Redirecting to Stripe checkout:', {
          sessionUrl: checkoutData.url,
          finalAmount: Math.round(currentTotal * 100)
        });
        window.location.href = checkoutData.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error("Failed to initiate checkout. Please try again.");
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-8">
      <CartSummary
        vipSeats={selectedSeats}
        vipTicketCount={vipTicketCount}
        standingTickets={standingTickets}
        earlyBirdTickets={earlyBirdTickets}
        vipPrice={event.price_vip}
        standingPrice={event.price_general_admission}
        earlyBirdPrice={event.early_bird_price || 0}
        discountPercentage={discountPercentage}
        event={event}
        onTotalChange={setCurrentTotal}
      />
      
      <div className="bg-gray-800/50 rounded-lg p-6">
        <h3 className="text-xl font-bold text-white mb-6">Your Information</h3>
        <CustomerForm
          onSubmit={handleCustomerSubmit}
          eventId={event.id}
          subtotal={selectedSeats.length * event.price_vip + 
                   standingTickets * event.price_general_admission + 
                   earlyBirdTickets * (event.early_bird_price || 0)}
          onPromoCodeApplied={setDiscountPercentage}
          eventCurrency={event.currency}
          renderQRPhButton={(form) => event.currency === 'PHP' ? (
            <QRPhButton
              event={event}
              form={form}
              vipSeats={selectedSeats}
              standingTickets={standingTickets}
              earlyBirdTickets={earlyBirdTickets}
              totalAmount={currentTotal}
            />
          ) : undefined}
        />
      </div>
    </div>
  );
};
